import { useEffect, useMemo, useRef, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnOrderState,
  type ColumnSizingState,
  type VisibilityState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Inbox, SearchX } from "lucide-react";
import { EmptyState } from "../components/EmptyState.js";
import { StatusPill } from "../components/StatusPill.js";
import { Button } from "../components/Button.js";
import type { SubmissionSort } from "@sga/shared";
import {
  PAGE_SIZES,
  useColumns,
  useSubmissions,
  type SubmissionRow,
  type SubmissionQueryState,
} from "./useSubmissions.js";

/**
 * The submissions table — docs/01-PRODUCT-SPEC.md §9.1.
 *
 * Virtualised because the target is 50,000 rows on screen without jank: only the ~30 rows in view
 * are in the DOM at any moment, and scrolling replaces their contents rather than mounting new
 * nodes. Rows arrive by cursor from `useInfiniteQuery`; the virtualiser asks for the next page when
 * the last rendered row comes within a screen of the end, so a coordinator scrolling fast never
 * sees the loading edge.
 */

const STICKY_COLUMN = "reference";

interface Props {
  formId: string;
  state: SubmissionQueryState;
  onSortChange: (sort: SubmissionSort) => void;
  onOpenDetail: (submissionId: string) => void;
  selection: Set<string>;
  onSelectionChange: (selection: Set<string>) => void;
  selectAllMatching: boolean;
  onSelectAllMatching: (value: boolean) => void;
  /** Lets the empty state tell "nothing submitted yet" apart from "your filter matched nothing". */
  onClearFilters?: () => void;
}

/**
 * A starting width per field type, because one width for every column is wrong in both directions:
 * a rating that holds "4" was given the same 180px as a paragraph answer, and the paragraph then
 * dragged its column across the screen. These are starting points only — every column is resizable,
 * and a drag is remembered for the session.
 */
const DEFAULT_WIDTHS: Record<string, number> = {
  // Free text that people write sentences into.
  text_multi: 340,
  rich_text: 340,
  address: 260,
  // Short, known shapes.
  rating: 90,
  nps: 90,
  slider: 90,
  number: 100,
  decimal: 100,
  currency: 110,
  yes_no: 100,
  decision_box: 100,
  checkbox: 100,
  date: 130,
  month_year: 130,
  time: 110,
  datetime: 170,
  phone: 150,
  unique_id: 150,
  random_id: 150,
  name: 200,
  email: 220,
  website: 220,
  multi_select: 220,
  large_list: 220,
};

export const widthForType = (type: string): number => DEFAULT_WIDTHS[type] ?? 180;

/** One line at 36px, four at 108px — the two row heights the wrap toggle switches between. */
const ROW_HEIGHT = { compact: 36, wrapped: 108 } as const;

/**
 * How far from the left edge a frozen column sits: the sum of the widths ahead of it. Both frozen
 * columns carried `left-0`, so the reference column was painted over the checkbox rather than
 * beside it — invisible until `table-fixed` made the widths real.
 */
export const stickyOffset = (widths: number[], index: number): number =>
  widths.slice(0, index).reduce((total, width) => total + width, 0);

/**
 * What the API calls the column a header sits over, and how it orders — or `null` when the column
 * cannot be sorted at all.
 *
 * The table's own column ids and the API's sort fields are two different vocabularies, and clicking
 * a header used to send the id straight through as a sort field. Three of them are not fields the
 * API knows: `select`, `reference` and `respondentName` were each rejected by
 * `submissionListQuerySchema` as "not a filterable field", the request 400'd, and the table
 * replaced itself with "Could not load submissions." — where it stayed, because the rejected sort
 * is held in state, until the page was reloaded. Clicking the select-all checkbox did it too: the
 * checkbox was rendered *inside* the sort button, so ticking it also asked to sort by `select`.
 */
export interface SortTarget {
  field: string;
  kind: "numeric" | "temporal" | "text";
}

const BUILT_IN_SORT: Record<string, SortTarget> = {
  // The column shows `uniqueId ?? randomId ?? id`; the reference number is the part worth ordering
  // by, and it is zero-padded, so it reads in sequence.
  reference: { field: "uniqueId", kind: "text" },
  respondentName: { field: "respondentName", kind: "text" },
  respondentEmail: { field: "respondentEmail", kind: "text" },
  reviewStatus: { field: "reviewStatus", kind: "text" },
  status: { field: "status", kind: "text" },
  submittedAt: { field: "submittedAt", kind: "temporal" },
  // `select` is deliberately absent — a column of checkboxes has no order.
};

export const sortTargetFor = (
  columnId: string,
  kinds: ReadonlyMap<string, SortTarget["kind"]> = new Map(),
): SortTarget | null => {
  if (columnId.startsWith("data.")) {
    return { field: columnId, kind: kinds.get(columnId) ?? "text" };
  }
  return BUILT_IN_SORT[columnId] ?? null;
};

/**
 * Where a click on a header takes the sort.
 *
 * A new column starts ascending — A first, 1 first, which is what "sort by name" means to the
 * person asking for it — except for dates, where the useful end is the recent one. Clicking the
 * column already sorted reverses it.
 */
export const nextSort = (current: SubmissionSort, target: SortTarget): SubmissionSort => {
  if (current.field === target.field) {
    return { field: target.field, direction: current.direction === "asc" ? "desc" : "asc" };
  }
  return { field: target.field, direction: target.kind === "temporal" ? "desc" : "asc" };
};

/**
 * What clicking this header will do, in words, for the hover text. A header that reorders the whole
 * table on click should say so before it is clicked.
 */
export const sortHint = (target: SortTarget, active: "asc" | "desc" | null): string => {
  const ends =
    target.kind === "text"
      ? (["A to Z", "Z to A"] as const)
      : target.kind === "temporal"
        ? (["oldest first", "newest first"] as const)
        : (["lowest first", "highest first"] as const);
  return `sort ${active === "asc" ? ends[1] : ends[0]}`;
};

/** A column header for a `title` attribute: these are authored questions and run long. */
const headerText = (header: unknown): string | undefined =>
  typeof header === "string" ? header : undefined;

export const formatCell = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map(formatCell).join(", ");
  return Object.values(value as Record<string, unknown>)
    .map(formatCell)
    .filter(Boolean)
    .join(" ");
};

export const SubmissionsTable = ({
  formId,
  state,
  onSortChange,
  onOpenDetail,
  selection,
  onSelectionChange,
  selectAllMatching,
  onSelectAllMatching,
  onClearFilters,
}: Props): JSX.Element => {
  const isFiltered = state.filters.length > 0 || Boolean(state.q);
  const columnsQuery = useColumns(formId);
  const submissions = useSubmissions(formId, state);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [chooserOpen, setChooserOpen] = useState(false);
  const [wrapText, setWrapText] = useState(false);
  const rowHeight = wrapText ? ROW_HEIGHT.wrapped : ROW_HEIGHT.compact;

  const rows = useMemo(() => submissions.data?.data ?? [], [submissions.data]);
  const piiMasked = submissions.data?.piiMasked ?? false;
  const { paging } = submissions;

  const columns = useMemo<ColumnDef<SubmissionRow>[]>(() => {
    const fieldColumns: ColumnDef<SubmissionRow>[] = (columnsQuery.data?.data ?? [])
      // An encrypted field has no value in any row, so a column for it would be a header over an
      // empty stripe. It is revealed one row at a time in the detail drawer, or exported in bulk.
      .filter((definition) => !definition.isSensitive)
      .map((definition) => ({
        id: definition.key,
        header: definition.masked ? `${definition.label} (masked)` : definition.label,
        size: widthForType(definition.type),
        minSize: 80,
        accessorFn: (row) => row.data[definition.key.replace(/^data\./, "")],
        cell: (context) => formatCell(context.getValue()),
      }));

    return [
      {
        id: "select",
        size: 44,
        enableResizing: false,
        header: () => (
          <input
            type="checkbox"
            aria-label="Select every row on this page"
            checked={rows.length > 0 && selection.size === rows.length}
            onChange={(event) => {
              onSelectionChange(
                event.target.checked ? new Set(rows.map((row) => row.id)) : new Set(),
              );
              if (!event.target.checked) onSelectAllMatching(false);
            }}
          />
        ),
        cell: (context) => (
          <input
            type="checkbox"
            aria-label={`Select ${context.row.original.reference ?? context.row.original.id}`}
            checked={selection.has(context.row.original.id)}
            onChange={(event) => {
              const next = new Set(selection);
              if (event.target.checked) next.add(context.row.original.id);
              else next.delete(context.row.original.id);
              onSelectionChange(next);
            }}
          />
        ),
      },
      {
        id: STICKY_COLUMN,
        header: "Reference",
        size: 150,
        accessorFn: (row) => row.reference ?? row.randomId ?? row.id.slice(0, 8),
      },
      // "Applicant", not "Name": these two describe the *person* — the canonical profile carried
      // across every form and year — while the field columns appended below describe what this
      // form asked. A form with its own "Name" or "Email" field (most of them) otherwise produces
      // two identically-headed columns and no way to tell which is which.
      {
        id: "respondentName",
        header: "Applicant",
        size: 180,
        accessorFn: (row) => row.respondentName ?? "",
      },
      {
        id: "respondentEmail",
        header: piiMasked ? "Applicant email (masked)" : "Applicant email",
        size: 220,
        accessorFn: (row) => row.respondentEmail ?? "",
      },
      {
        id: "reviewStatus",
        header: "Review",
        size: 120,
        accessorFn: (row) => row.reviewStatus,
        cell: (context) => <StatusPill status={String(context.getValue())} />,
      },
      {
        id: "status",
        header: "Status",
        size: 120,
        accessorFn: (row) => row.status,
        cell: (context) => <StatusPill status={String(context.getValue())} />,
      },
      {
        id: "submittedAt",
        header: "Submitted",
        size: 170,
        accessorFn: (row) =>
          row.submittedAt
            ? // Africa/Lagos at the presentation layer only — the value on the wire is UTC.
              new Date(row.submittedAt).toLocaleString("en-NG", { timeZone: "Africa/Lagos" })
            : "",
      },
      ...fieldColumns,
    ];
  }, [columnsQuery.data, piiMasked, rows, selection, onSelectionChange, onSelectAllMatching]);

  const table = useReactTable({
    data: rows,
    columns,
    state: { columnVisibility, columnOrder, columnSizing },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
  });

  // Recomputed from the live sizes rather than the constants, so dragging the checkbox or
  // reference column keeps the frozen pair correctly abutted.
  const frozenWidths = table
    .getVisibleLeafColumns()
    .slice(0, 2)
    .map((column) => column.getSize());

  const tableRows = table.getRowModel().rows;
  const virtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollRef.current,
    // Both modes are a fixed height, so the virtualiser needs no per-row measurement: wrapped rows
    // clamp at four lines rather than growing to fit, which keeps scrolling honest at 50,000 rows.
    estimateSize: () => rowHeight,
    overscan: 12,
  });

  // Switching between compact and wrapped changes every row's height, and the virtualiser holds
  // measurements from before the switch — without this the spacer rows keep the old geometry and
  // the body scrolls out of step with the header.
  useEffect(() => {
    virtualizer.measure();
  }, [virtualizer, rowHeight]);

  // Reaching the end of a page is now the end of the page, not a request for more. The rows still
  // go through the virtualiser: 200 rows at four lines each is 21,600px of table, and the point of
  // only building what is on screen holds whether the rest is one page away or a scroll away.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [paging.pageNumber, paging.pageSize]);

  // Keyed by the table's column id (`data.<key>`), which is what a header knows about itself.
  const sortKinds = useMemo(
    () =>
      new Map(
        (columnsQuery.data?.data ?? []).map(
          (definition) => [definition.key, definition.sortKind] as const,
        ),
      ),
    [columnsQuery.data],
  );

  if (submissions.isError) {
    return (
      <p role="alert" className="rounded-md border border-brand-red/40 bg-brand-red/5 p-4 text-sm">
        Could not load submissions. {(submissions.error as Error).message}
      </p>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center justify-between gap-3 border-b px-1 py-2 text-sm">
        <div className="flex items-center gap-3">
          {/* "100 loaded+" told a coordinator almost nothing — not how many there are, not whether
              the one they are looking for is among them. This is the whole count, filtered. */}
          <span className="text-muted-foreground">
            {paging.total === 0
              ? "No submissions"
              : `Showing ${paging.firstRow.toLocaleString()}–${paging.lastRow.toLocaleString()} of ${paging.total.toLocaleString()}`}
          </span>
          {selection.size > 0 && (
            <span className="flex items-center gap-2">
              <strong>{selection.size.toLocaleString()} selected</strong>
              {paging.total > rows.length && !selectAllMatching && (
                <button
                  type="button"
                  className="underline"
                  onClick={() => onSelectAllMatching(true)}
                >
                  Select all rows matching this filter
                </button>
              )}
              {selectAllMatching && (
                <span className="rounded bg-accent/20 px-2 py-0.5 text-primary">
                  Everything matching the current filter is selected
                </span>
              )}
            </span>
          )}
          {piiMasked && (
            <span className="rounded bg-muted px-2 py-0.5 text-muted-foreground">
              Personal data is masked for your role
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setWrapText((wrap) => !wrap)}
            aria-pressed={wrapText}
            title="Show long answers over four lines instead of one"
            className={`rounded-md border px-3 py-1.5 hover:bg-muted ${
              wrapText ? "bg-muted font-medium" : ""
            }`}
          >
            {wrapText ? "Compact rows" : "Wrap text"}
          </button>

          <div className="relative">
            <button
              type="button"
              onClick={() => setChooserOpen((open) => !open)}
              aria-expanded={chooserOpen}
              className="rounded-md border px-3 py-1.5 hover:bg-muted"
            >
              Columns
            </button>
            {chooserOpen && (
              <div className="absolute right-0 z-20 mt-1 max-h-80 w-64 overflow-auto rounded-md border bg-background p-2 shadow-lg">
                {table.getAllLeafColumns().map((column) => (
                  <label key={column.id} className="flex items-center gap-2 px-1 py-1 text-sm">
                    <input
                      type="checkbox"
                      checked={column.getIsVisible()}
                      onChange={column.getToggleVisibilityHandler()}
                    />
                    {typeof column.columnDef.header === "string"
                      ? column.columnDef.header
                      : column.id}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-auto"
        role="region"
        aria-label="Submissions"
        tabIndex={0}
      >
        {/* `table-fixed` is what makes every width below mean anything. Under the browser's default
            auto layout a `width` on a cell is only a suggestion, so one long paragraph answer
            stretched its column past the edge of the screen, dragged the header into a tall narrow
            ribbon, and left the resize handles apparently doing nothing. Fixed layout honours the
            declared width exactly, which is also what lets `truncate` have a box to truncate to. */}
        <table
          className="table-fixed border-collapse text-sm"
          style={{ width: table.getTotalSize() }}
        >
          <thead className="sticky top-0 z-10 bg-background">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  const target = sortTargetFor(header.column.id, sortKinds);
                  const active = target?.field === state.sort.field ? state.sort.direction : null;
                  const label = headerText(header.column.columnDef.header);
                  return (
                    <th
                      key={header.id}
                      // Announced by a screen reader, and the only thing that tells a sighted user
                      // which of thirty columns the table is currently ordered by.
                      aria-sort={
                        active === "asc"
                          ? "ascending"
                          : active === "desc"
                            ? "descending"
                            : undefined
                      }
                      style={{
                        width: header.getSize(),
                        ...(index <= 1 ? { left: stickyOffset(frozenWidths, index) } : {}),
                      }}
                      className={`relative border-b px-2 py-2 text-left font-medium ${
                        index <= 1 ? "sticky z-10 bg-background" : ""
                      }`}
                    >
                      {target ? (
                        <button
                          type="button"
                          className="flex w-full items-center gap-1 pr-2 text-left hover:text-primary"
                          // The full label, for a header too long for its column. Every one of these
                          // is a question someone wrote, and they run to a sentence more often than not.
                          title={`${label ?? ""}${label ? " — " : ""}${sortHint(target, active)}`}
                          onClick={() => onSortChange(nextSort(state.sort, target))}
                        >
                          <span className="truncate">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                          {/* The arrow is what makes the sort visible at all — without it a
                            coordinator had no way to tell a sorted table from an unsorted one, and
                            no reason to think the headers were clickable. Held at half opacity on
                            the inactive columns so they advertise that they can be clicked. */}
                          <span
                            aria-hidden
                            className={`shrink-0 text-xs ${active ? "text-primary" : "opacity-0 group-hover:opacity-40"}`}
                          >
                            {active === "asc" ? "\u2191" : active === "desc" ? "\u2193" : "\u2195"}
                          </span>
                        </button>
                      ) : (
                        <span className="block truncate pr-2" title={label}>
                          {/* Not wrapped in the sort button: the select-all checkbox lives here, and
                            an input inside a button both nests interactive content and sent every
                            tick through as a request to sort by a column that has no order. */}
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </span>
                      )}
                      {header.column.getCanResize() && (
                        <span
                          role="separator"
                          aria-orientation="vertical"
                          // A 4px hairline in border grey read as part of the rule between columns
                          // rather than as something to grab. Wider, and it lights up under the
                          // cursor so the column is visibly adjustable.
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          onDoubleClick={() => header.column.resetSize()}
                          title="Drag to resize · double-click to reset"
                          className={`absolute right-0 top-0 h-full w-1.5 touch-none select-none bg-border hover:bg-primary ${
                            header.column.getIsResizing() ? "bg-primary" : ""
                          } cursor-col-resize`}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {/* Spacer rows stand in for everything scrolled past, so the scrollbar reflects the
                whole result set while the DOM holds only what is visible. */}
            <tr style={{ height: virtualizer.getVirtualItems()[0]?.start ?? 0 }} aria-hidden />
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const row = tableRows[virtualRow.index];
              if (!row) return null;
              return (
                <tr
                  key={row.id}
                  style={{ height: rowHeight }}
                  className="cursor-pointer border-b hover:bg-muted/40"
                  onClick={() => onOpenDetail(row.original.id)}
                >
                  {row.getVisibleCells().map((cell, index) => {
                    // The raw text, for the hover tooltip: a cell may render a pill or a checkbox,
                    // and `title` needs the value rather than the node.
                    const full = formatCell(cell.getValue());
                    return (
                      <td
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                          ...(index <= 1 ? { left: stickyOffset(frozenWidths, index) } : {}),
                        }}
                        className={`overflow-hidden px-2 ${wrapText ? "align-top" : "align-middle"} ${
                          index <= 1 ? "sticky z-[1] bg-background" : ""
                        }`}
                        title={full || undefined}
                      >
                        <div className={wrapText ? "line-clamp-4 break-words" : "truncate"}>
                          {flexRender(
                            cell.column.columnDef.cell ?? ((c) => String(c.getValue() ?? "")),
                            cell.getContext(),
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            <tr
              style={{
                height:
                  virtualizer.getTotalSize() - (virtualizer.getVirtualItems().at(-1)?.end ?? 0),
              }}
              aria-hidden
            />
          </tbody>
        </table>

        {/* An empty table of bare column headers reads as broken. Which of the two messages applies
            matters: "nothing has been submitted" and "your filter matched nothing" call for
            completely different next actions, and only the second one is the user's own doing. */}
        {!submissions.isPending && rows.length === 0 && (
          <div className="p-6">
            {isFiltered ? (
              <EmptyState
                icon={SearchX}
                title="No submissions match these filters"
                description="Nothing here matched the search or filters you set. Clear them to see every submission on this form."
                action={
                  onClearFilters && (
                    <Button variant="secondary" onClick={onClearFilters}>
                      Clear filters
                    </Button>
                  )
                }
              />
            ) : (
              <EmptyState
                icon={Inbox}
                title="No submissions yet"
                description="Answers appear here as soon as someone completes the form. Share the form's public link to start collecting them."
              />
            )}
          </div>
        )}
      </div>

      {/* The pager. Below the table rather than above it, because the reason to reach for it is
          having read to the bottom. */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t px-1 py-2 text-sm">
        <label className="flex items-center gap-2 text-muted-foreground">
          Rows per page
          <select
            value={paging.pageSize}
            onChange={(event) => paging.setPageSize(Number(event.target.value))}
            className="rounded-md border px-2 py-1"
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>

        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">
            Page {paging.pageNumber.toLocaleString()}
            {paging.total > 0 &&
              ` of ${Math.max(1, Math.ceil(paging.total / paging.pageSize)).toLocaleString()}`}
          </span>
          <button
            type="button"
            onClick={paging.goPrevious}
            disabled={!paging.hasPrevious || submissions.isFetching}
            className="rounded-md border px-3 py-1 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={paging.goNext}
            disabled={!paging.hasNext || submissions.isFetching}
            className="rounded-md border px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};
